import { useCallback, useEffect, useMemo, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useStreamFunctions } from 'stream/globalFunction/useStreamFunctions';
import { z } from 'zod';

import {
  getUniqueFunctionName,
  validatePythonVariable,
} from '@polyai/common/lib/stringHelper';
import type {
  CreateFunctionCommand as CreateFunctionPayload,
  CreateEndFunctionCommand as EndFunctionCreatePayload,
  UpdateEndFunctionCommand as EndFunctionUpdatePayload,
  CreateStartFunctionCommand as StartFunctionCreatePayload,
  UpdateStartFunctionCommand as StartFunctionUpdatePayload,
  UpdateFunctionCommand as UpdateFunctionPayload,
} from '@polyai/schemas';
import {
  END_FUNCTION_NAME,
  START_FUNCTION_NAME,
} from '@polyai/stream/commands/handlers/special-functions/constants';
import type { Flow } from '@polyai/stream/store/reducers/flows';
import { flowFunctionsSelectors } from '@polyai/stream/store/reducers/flows';
import { functionsReferencesAdapter } from '@polyai/stream/store/reducers/functions/adapters';
import { parametersSelectors } from '@polyai/stream/store/reducers/functions/selectors';
import type { Function } from '@polyai/stream/store/reducers/functions/types';
import type { SpecialFunction } from '@polyai/stream/store/reducers/specialFunctions';
import {
  createVariable,
  selectVariables,
} from '@polyai/stream/store/reducers/variables';
import { variablesReferencesAdapter } from '@polyai/stream/store/reducers/variables/utils';

import type { FunctionType } from 'api/types';
import { useServerDispatch } from 'components/organisms/AgentStream/context/AgentStreamContext';
import { useAppSelector } from 'hooks/reduxHooks';
import { generateVariableId } from 'lib/prettyUuid';
import useFlowState from 'screens/FlowDetails/hooks/useFlowState';
import useFlowsState from 'screens/FlowDetails/hooks/useFlowsState';
import type { StreamFunctionForm } from '../types';
import { streamFunctionSchema } from '../types';
import {
  extractVariableNames,
  formatPostFunction,
  getDefaultValues,
  getFunctionType,
  isValidPythonParamName,
  updateFunctionSignatureInCode,
} from '../utils';

interface UseFunctionForm {
  func?: Function | SpecialFunction;
  defaultName?: string;
  defaultDescription?: string;
  functionType?: FunctionType;
  onSubmitSuccessful?: (functionId: string) => void;
}

const useFunctionForm = ({
  func,
  defaultName,
  defaultDescription,
  functionType: passedFunctionType,
  onSubmitSuccessful,
}: UseFunctionForm) => {
  const functionType = passedFunctionType ?? getFunctionType(func?.name ?? '');

  const {
    createFunction: dispatchCreateFunctionCommand,
    createStartFunction,
    createEndFunction,
    activeFunctions,
    startFunction,
    endFunction,
    updateFunction: dispatchUpdateFunctionCommand,
    updateStartFunction,
    updateEndFunction,
  } = useStreamFunctions();
  const { flows } = useFlowsState();
  const { flow } = useFlowState();
  const variables = useAppSelector(selectVariables);
  const serverDispatch = useServerDispatch();

  const relevantFunctionNames = useMemo(() => {
    const globalFunctionNames = activeFunctions
      .filter(f => f.id !== func?.id)
      .map(f => f.name);
    const getFlowFunctionNames = (flow: Flow) =>
      flowFunctionsSelectors
        .selectAll(flow.transitionFunctions)
        .filter(t => t.id !== func?.id && !t.archived)
        .map(t => t.name);
    if (functionType !== 'flow_function') {
      return [...globalFunctionNames, ...flows.flatMap(getFlowFunctionNames)];
    }
    return [...activeFunctions.map(f => f.name), ...getFlowFunctionNames(flow)];
  }, [activeFunctions, functionType, flow, func?.id, flows]);

  const defaultValues = useMemo(() => {
    return getDefaultValues({
      func,
      defaultName:
        defaultName ??
        getUniqueFunctionName('function', [
          'function',
          ...relevantFunctionNames,
        ]),
      functionType,
      defaultDescription,
    });
  }, [
    func,
    defaultName,
    relevantFunctionNames,
    functionType,
    defaultDescription,
  ]);

  // Final form schema that will be submitted after being parsed
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  const finalSchema = streamFunctionSchema.superRefine((values, ctx) => {
    const exists = relevantFunctionNames.find(name => name === values.name);

    // Function name validation
    if (exists) {
      ctx.addIssue({
        path: ['name'],
        code: z.ZodIssueCode.custom,
        message: 'Function name is already taken',
      });
    }

    const pythonVariableError = validatePythonVariable(values.name);
    if (pythonVariableError !== true) {
      ctx.addIssue({
        path: ['name'],
        code: z.ZodIssueCode.custom,
        message: pythonVariableError,
      });
    }

    // Function description validation
    const funcType = getFunctionType(values.name);
    const isSpecialFunction =
      funcType === 'conversation_end' || funcType === 'conversation_start';

    if (!isSpecialFunction) {
      if (!values.description || values.description.trim().length === 0) {
        ctx.addIssue({
          path: ['description'],
          code: z.ZodIssueCode.custom,
          message: 'Description is required',
        });
      }
    }

    // Function parameters validation
    const parameters = values.parameters;
    if (parameters) {
      // Last parameter is a dummy one
      for (let i = 0; i < parameters.length - 1; i++) {
        if (!isValidPythonParamName(parameters[i].name)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['parameters', i, 'name'],
            message: `Parameter name: ${parameters[i].name} is invalid`,
          });
        }
        if (
          !parameters[i].description ||
          parameters[i].description.trim() === ''
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['parameters', i, 'description'],
            message: 'Parameter description is required',
          });
        }
      }
    }
    return true;
  });

  const methods = useForm<StreamFunctionForm>({
    resolver: zodResolver(finalSchema),
    defaultValues,
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const previousParametersRef = useRef<string>('');
  const saveFunctionRef = useRef<((data: StreamFunctionForm) => void) | null>(
    null,
  );

  useEffect(() => {
    if (func) {
      const currentParams = parametersSelectors.selectAll(func.parameters);
      previousParametersRef.current = JSON.stringify(
        currentParams.map(p => ({ name: p.name, type: p.type })),
      );
    }
  }, [func]);

  const findOrCreateVariables = useCallback(
    (variableNames: string[]) => {
      const variableIds: string[] = [];

      variableNames.forEach(variableName => {
        const variable = variables.find(v => v.name === variableName);
        if (variable) {
          variableIds.push(variable.id);
        } else {
          const variableId = generateVariableId();
          void serverDispatch(
            createVariable({
              id: variableId,
              name: variableName,
              references: variablesReferencesAdapter.createInitialState(),
            }),
          );
          variableIds.push(variableId);
        }
      });

      return variableIds;
    },
    [variables, serverDispatch],
  );

  const createAndGetCodeRefs = useCallback(
    (code: string, existing?: Function | SpecialFunction) => {
      const references = functionsReferencesAdapter.createInitialState(
        (existing as Function)?.references,
      );

      const variableNames = extractVariableNames(code);
      const variableIds = findOrCreateVariables(variableNames);

      references.variables = {};
      variableIds.forEach(variableId => {
        references.variables[variableId] = true;
      });
      return references;
    },
    [findOrCreateVariables],
  );

  const createFunction = useCallback(
    (
      data: StreamFunctionForm,
      isStartFunction: boolean,
      isEndFunction: boolean,
    ) => {
      const formattedFunction = formatPostFunction(data);
      const references = createAndGetCodeRefs(formattedFunction.code ?? '');

      if (isStartFunction) {
        const startFunctionCreatePayload: StartFunctionCreatePayload = {
          ...formattedFunction,
          references,
          parameters: [],
        };
        void createStartFunction(startFunctionCreatePayload);
      } else if (isEndFunction) {
        const endFunctionCreatePayload: EndFunctionCreatePayload = {
          ...formattedFunction,
          references,
          parameters: [],
        };
        void createEndFunction(endFunctionCreatePayload);
      } else {
        const createFunctionPayload: CreateFunctionPayload = {
          ...formattedFunction,
          references,
          parameters: formattedFunction.parameters ?? [],
        };
        void dispatchCreateFunctionCommand(createFunctionPayload);
      }
    },
    [
      createAndGetCodeRefs,
      createStartFunction,
      createEndFunction,
      dispatchCreateFunctionCommand,
    ],
  );

  const updateFunction = useCallback(
    (
      data: StreamFunctionForm,
      isStartFunction: boolean,
      isEndFunction: boolean,
    ) => {
      let updatedCode = data.code || '';

      if (func && func.name !== data.name) {
        const oldFunctionDef = `def ${func.name}(`;
        const newFunctionDef = `def ${data.name}(`;
        updatedCode = updatedCode.replaceAll(oldFunctionDef, newFunctionDef);
      }

      const formatted = formatPostFunction(data);

      if (!isStartFunction && !isEndFunction && func) {
        const oldParameters = parametersSelectors.selectAll(func.parameters);
        const newParameters = formatted.parameters ?? [];

        const oldParamsMap = new Map(oldParameters.map(p => [p.name, p.type]));
        const newParamsMap = new Map(newParameters.map(p => [p.name, p.type]));

        const parametersChanged =
          oldParamsMap.size !== newParamsMap.size ||
          Array.from(newParamsMap.entries()).some(
            ([name, type]) => oldParamsMap.get(name) !== type,
          ) ||
          Array.from(oldParamsMap.keys()).some(name => !newParamsMap.has(name));

        if (parametersChanged) {
          updatedCode = updateFunctionSignatureInCode(
            updatedCode,
            formatted.name,
            newParameters,
          );
        }
      }

      const commonPayload = {
        id: formatted.id,
        archived: formatted.archived,
        description: formatted.description,
        errors: { errors: formatted.errors ?? [] },
        code: updatedCode,
        references: createAndGetCodeRefs(updatedCode, func),
      };

      if (isStartFunction) {
        void updateStartFunction(commonPayload as StartFunctionUpdatePayload);
      } else if (isEndFunction) {
        void updateEndFunction(commonPayload as EndFunctionUpdatePayload);
      } else {
        const updatePayload: UpdateFunctionPayload = {
          ...commonPayload,
          name: formatted.name,
          parameters: { parameters: formatted.parameters ?? [] },
        };
        void dispatchUpdateFunctionCommand(updatePayload);
      }
    },
    [
      func,
      dispatchUpdateFunctionCommand,
      updateStartFunction,
      updateEndFunction,
      createAndGetCodeRefs,
    ],
  );

  const saveFunction = useCallback(
    (data: StreamFunctionForm) => {
      const isStartFunction = data.name === START_FUNCTION_NAME;
      const isEndFunction = data.name === END_FUNCTION_NAME;
      const functionExists = activeFunctions.find(f => f.id === data.id);
      const isUpdating =
        (isStartFunction && startFunction) ||
        (isEndFunction && endFunction) ||
        functionExists;

      if (isUpdating) {
        updateFunction(data, isStartFunction, isEndFunction);
      } else {
        createFunction(data, isStartFunction, isEndFunction);
        // Navigate to the newly created function's edit page
        if (data.id && onSubmitSuccessful) {
          onSubmitSuccessful(data.id);
        }
      }
    },
    [
      activeFunctions,
      startFunction,
      endFunction,
      updateFunction,
      createFunction,
      onSubmitSuccessful,
    ],
  );

  saveFunctionRef.current = saveFunction;

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing
  useEffect(() => {
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    const subscription = methods.watch((value, { name: fieldName }) => {
      if (
        fieldName === 'name' &&
        func &&
        value.name &&
        func.name !== value.name
      ) {
        const currentCode = value.code || '';
        const oldFunctionName = func.name;
        const newFunctionName = value.name;

        const functionDefPattern = new RegExp(
          `^def\\s+${oldFunctionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\([^)]*\\)\\s*:`,
          'm',
        );

        if (functionDefPattern.test(currentCode)) {
          const updatedCode = currentCode.replace(functionDefPattern, match =>
            match.replace(`def ${oldFunctionName}`, `def ${newFunctionName}`),
          );
          methods.setValue('code', updatedCode, { shouldDirty: false });
        }
      }

      if (fieldName === 'parameters' && func && value.id && value.name) {
        const funcType = getFunctionType(value.name);
        const isSpecialFunction =
          funcType === 'conversation_start' || funcType === 'conversation_end';

        if (!isSpecialFunction && value.id && value.name) {
          const cleanParams = (value.parameters ?? [])
            .filter((p): p is NonNullable<typeof p> => !!p && !!p.name)
            .map(p => ({
              id: p.id!,
              name: p.name!,
              description: p.description!,
              type: p.type!,
            }));
          const formatted = formatPostFunction({
            id: value.id,
            name: value.name,
            description: value.description ?? '',
            code: value.code ?? '',
            parameters: cleanParams,
            errors: (value.errors ?? []).filter(
              (e): e is NonNullable<typeof e> => !!e,
            ) as Array<{ message: string; lineno: number; text: string }>,
            archived: value.archived ?? false,
          });
          const oldParameters = parametersSelectors.selectAll(func.parameters);
          const newParameters = formatted.parameters ?? [];

          const oldParamsMap = new Map(
            oldParameters.map(p => [p.name, p.type]),
          );
          const newParamsMap = new Map(
            newParameters.map(p => [p.name, p.type]),
          );

          const parametersChanged =
            oldParamsMap.size !== newParamsMap.size ||
            Array.from(newParamsMap.entries()).some(
              ([name, type]) => oldParamsMap.get(name) !== type,
            ) ||
            Array.from(oldParamsMap.keys()).some(
              name => !newParamsMap.has(name),
            );

          const currentParamsKey = JSON.stringify(
            newParameters.map(p => ({ name: p.name, type: p.type })),
          );

          if (
            parametersChanged &&
            previousParametersRef.current !== currentParamsKey
          ) {
            previousParametersRef.current = currentParamsKey;
            const functionExists = activeFunctions.find(f => f.id === value.id);
            const isUpdating =
              (value.name === START_FUNCTION_NAME && startFunction) ||
              (value.name === END_FUNCTION_NAME && endFunction) ||
              functionExists;

            if (isUpdating && saveFunctionRef.current) {
              setTimeout(() => {
                void methods.handleSubmit(data => {
                  saveFunctionRef.current?.(data);
                })();
              }, 0);
            }
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [
    methods,
    func,
    functionType,
    activeFunctions,
    startFunction,
    endFunction,
  ]);

  return {
    methods,
    defaultValues,
    saveFunction,
  };
};

export default useFunctionForm;
